import { Card, Typography, TextField, Divider, FormGroup, MenuItem, Stack, Grid, Badge, IconButton, Tooltip, Box } from "@mui/material";
import { useGetMainList, useGetOne, useRecordContext } from "ra-core";
import { useEffect, ChangeEvent, FC, useCallback, useMemo, useState } from "react";
import { useHistory } from 'react-router-dom'
import { RecPatch } from "@/dataProvider/tools";
import { EncryptionMappingResponseDto, TableSchemaResponseDto } from "@/gen/api/governance";
import { useMutateEncryptionMappingsContext } from "@/pages/obfuscation/mutateEncryptionMappingsContext";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { BasicInfoRow, BasicInfoPiece } from "../../backups/BackupBasicInfoCard";
import MappedIcon from "@/icons/Mapped";
import SkippedIcon from "@/icons/Skipped";
import PendingReviewIcon from "@/icons/PendingReview";
import CopyTableMappings from "./CopyTableMappings";
import { useDispatch } from "react-redux";
import { crudGetOneEx } from "@/actions/crudFetchEx";
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";
import { E_FieldMapping, ET_FieldMapping } from "./FieldsMappingList.events";



export const TableSelectionCard: FC<{ useSource: 'tables'|'pending-tables' }> = ({ useSource }) => {

  const record = useRecordContext<EncryptionMappingResponseDto & RecPatch>()
  const { data: dbTablesMap, refetch: tbRefetch } = 
    useGetMainList<TableSchemaResponseDto & RecPatch>(!(record?.databaseId) ? 'noop' : useSource, pages(), sorts(), { dbId: record?.databaseId })
  const dbTables = useMemo(() => 
    Object.values(dbTablesMap || {})
      .sort((a, b) => a.schema === b.schema 
        ? ((a.name || '') < (b.name || '') ? -1 : 1)
        : ((a.schema || '') < (b.schema || '') ? -1 : 1)), [dbTablesMap])
  
  const { currTable, setCurrTable } = useMutateEncryptionMappingsContext()
  const [states, setStates] = useState({
    mappingAmend: {...initialAmend},
    book: new Map<number, [number, number, number]>()
  })
  const { data: fieldsCountsData } = useGetOne<FieldsCounts & RecPatch>(currTable ? 'db-fields-mapping-count' : 'noop', currTable?.databaseId || -1)
  const fieldsCounts: FieldsCounts = fieldsCountsData || {}
  
  const handleTableChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selectedTable = dbTables.find(x => x.tableId === +e.target.value)
    setCurrTable(selectedTable)
  }, [dbTables, setCurrTable])
  
  const history = useHistory()
  useEffect(() => {
    if (!currTable && dbTables.length > 0) {
      setCurrTable(dbTables[0])
    }
    if (currTable) {
      if (dbTables.length === 0) {
        history.goBack()
      } else {
        const tb = dbTables.find(d => d.id === currTable.tableId) ?? dbTables[0]
        setCurrTable(tb)
      }
    }
  }, [currTable, setCurrTable, history, dbTables])

  const dispatch = useDispatch()
  const tbSelectRefetch = useCallback(() => {
    tbRefetch()
    dispatch(crudGetOneEx('db-fields-mapping-count', currTable?.databaseId ?? -1, ''))
  }, [currTable?.databaseId, dispatch, tbRefetch])

  const eventBus = useEventBus('fields-mapping-list')
  useEffect(() => {
    eventBus.subscribe(E_FieldMapping.EncryptMappingStatusChange, (data: ET_FieldMapping['encrypt_mapping_status_change']) => {
      const maintain = (book: Map<number, [number, number, number]>) => {
        for (const x of data) {
          if (x.fromCata === x.toCata) {
            book.delete(x.fieldId)
          } else {
            const entry = [0, 0, 0] as [number, number, number]
            const getI = (x: FieldMappingCatagory) =>
                x === 'mapped' ? 0
              : x === 'skip' ? 1
              : 2
            entry[getI(x.fromCata)] = -1
            entry[getI(x.toCata)] = 1
            book.set(x.fieldId, entry)
          }
        }
      }

      setStates(v => {
        const v1 = {...v}
        maintain(v1.book)
        v1.mappingAmend = v.book.values().reduce((acc, x) => {
          acc.mapped += x[0]
          acc.skip += x[1]
          acc['pending-review'] += x[2]
          return acc
        }, {...initialAmend} as MappingAmend)
        return v1
      })
    })

    eventBus.subscribe(E_FieldMapping.Reset, () => {
      setStates({ mappingAmend: {...initialAmend}, book: new Map() })
    })
    eventBus.subscribe(E_FieldMapping.CurrTableChanged, () => {
      setStates({ mappingAmend: {...initialAmend}, book: new Map() })
    })
  }, [eventBus])

  useEffect(() => {
    const t0 = eventBus.subscribe(E_FieldMapping.MergeDelta, () => {
      const t = dbTables.find(x => x.id === currTable?.tableId)
      if (t) {
        t.pendingReviewCount = (t.pendingReviewCount ?? 0) + states.mappingAmend["pending-review"]
        t.mappedCount = (t.mappedCount ?? 0) + states.mappingAmend.mapped
      }
      setStates({ mappingAmend: {...initialAmend}, book: new Map() })
    })
    return () => {
      eventBus.unsubscribe(t0)
    }
  }, [currTable?.tableId, dbTables, eventBus, states.mappingAmend])

  return (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Database
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.databaseName}
          </Typography>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Engine
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.engineVersion + ' ' + (record?.engineEdition || '')}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      <Divider />
      <Grid
        container
        sx={{
          px: 3,
          py: 1,
          alignItems: 'center',
        }}
      >
        <Grid item xs={8}>
          <Grid container>
            <Grid item xs={3}>            
              <Typography
                id="table-name"
                color="textPrimary"
                variant="h6"
                sx={{ flex: 1 }}
              >
                Table Name
              </Typography>
            </Grid>            
            <Grid item xs={9} sx={listSx}>
              <FormGroup>
                {useMemo(() => 
                  <TextField
                    value={currTable?.tableId || dbTables[0]?.tableId || ''}
                    onChange={handleTableChange}
                    select
                    size="small"
                    id='table-select'
                    SelectProps={{
                      inputProps: {
                        labelId: 'table-name',
                      },
                      MenuProps: { PaperProps: { sx: listSx }, keepMounted: true },
                    }}
                  >
                    {dbTables.map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        <Tooltip title={`${t.schema}.${t.name}`}>
                          <Box className="gcontainer"
                            sx={{ 
                              width: '100%',
                              '& span.MuiTypography-body2': { 
                                maxWidth: 320,
                                wordBreak: 'keep-all',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                mr: 'auto',
                              }
                            }}
                          >
                            <Typography component="span" variant="caption" color="InactiveCaptionText"
                              className="i1"
                            >
                              {t.schema}.
                            </Typography>
                            <Typography component="span" variant="body2" 
                              className="i2"                        
                            >
                              {t.name}
                            </Typography>
                            <Typography component="span" variant="caption" color="success.main"
                              className="i3"
                            >
                              <Badge 
                                color={"success" as any} 
                                badgeContent={t.mappedCount} 
                                // showZero 
                                max={9999} 
                              />
                            </Typography>
                            <Typography component="span" variant="caption"
                              className="i4"
                              sx={{ color: 'warning.light', mr: 2 }}
                            >
                              <Badge 
                                color={"warning" as any} 
                                badgeContent={t.id === currTable?.tableId 
                                  ? ((t.pendingReviewCount || 0) + states.mappingAmend["pending-review"]) 
                                  : (t.pendingReviewCount || 0)} 
                                // showZero 
                                max={9999} 
                                sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                              />
                            </Typography>
                          </Box>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </TextField>
                , [currTable?.tableId, dbTables, handleTableChange, states.mappingAmend])}
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex' }}>
          {record && currTable && 
            <CopyTableMappings destDatabaseName={record.databaseName!} destTable={currTable} tbSelectRefetch={tbSelectRefetch}/>}
          <Stack 
            direction="row" 
            spacing={4} 
            sx={{ 
              mx: 'auto',
              '& span.MuiBadge-badge': { whiteSpace: 'nowrap' },
            }} 
          >
            <Tooltip title="Mapped">
              <IconButton color={"inherit" as any} size="small">
                <Badge 
                  color={"success" as any} 
                  badgeContent={'' + 
                    (fieldsCounts.mappedCount || 0) + 
                    (states.mappingAmend.mapped !== 0 
                      ? ' ' + toSignedString(states.mappingAmend.mapped)
                      : '')}
                  showZero 
                  max={9999}
                >
                  <MappedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Skipped">
              <IconButton color={"inherit"} size="small">
                <Badge 
                  color="secondary" 
                  badgeContent={'' + 
                    (fieldsCounts.skippedCount || 0) + 
                    (states.mappingAmend.skip !== 0 
                      ? ' ' + toSignedString(states.mappingAmend.skip)
                      : '')}
                  showZero 
                  max={9999}
                >
                  <SkippedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pending Review">
              <IconButton color={"inherit" as any} size="small">
                <Badge 
                  color={"warning" as any} 
                  badgeContent={'' + 
                    (fieldsCounts.pendingReviewCount || 0) + 
                    (states.mappingAmend["pending-review"] !== 0 
                      ? ' ' + toSignedString(states.mappingAmend["pending-review"])
                      : '')}
                  showZero 
                  max={9999}
                  sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                >
                  <PendingReviewIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  )
}

const listSx = {
  '& .gcontainer': {                          
    display: 'grid',
    gridAutoColumns: 'minmax(50px, auto) 1fr minmax(20px, auto) minmax(20px, auto)',
    columnGap: 0.5,
    '& .i1': {
      lineHeight: '1.8',
      textAlign: 'right',
      gridColumn: '1 / span 1'
    },
    '& .i2': { gridColumn: '2 / span 1' },
    '& .i3': { gridColumn: '3 / span 1' },
    '& .i4': { gridColumn: '4 / span 1' }
  }
}

const toSignedString = (x:number) => (Math.abs(x) === x ? '+' : '-') + Math.abs(x)

interface FieldsCounts {
  mappedCount?: number;
  skippedCount?: number;
  pendingReviewCount?: number;
}

type FieldMappingCatagory = 'mapped' | 'skip' | 'pending-review'

type MappingAmend = {
  [k in FieldMappingCatagory]: number;
}

const initialAmend: MappingAmend = {
  mapped: 0,
  skip: 0,
  'pending-review': 0,
}
